import { useMemo } from 'react'
import { Link, useParams } from 'react-router-dom'
import { useQuery, keepPreviousData } from '@tanstack/react-query'
import { categorySlug } from '../lib/blogCategory'
import { Clock, ChevronRight, ArrowRight } from 'lucide-react'
import { PageWrapper } from '../components/layout/PageWrapper'
import { Seo } from '../components/Seo'
import { fetchBlogPost, type BlogListItem, type BlogPostFull } from '../lib/api/blog'
import { useAppConfig } from '../hooks/useAppConfig'

// SSR primes this so the article renders instantly (no loading skeleton -> no
// footer CLS, faster LCP). See ssrMeta injectMeta.
declare global {
  interface Window { __BLOG_POST__?: { post: BlogPostFull; related: BlogListItem[] } }
}

const SITE = 'https://fixdelivered.com'

function fmtDate(iso?: string): string {
  if (!iso) return ''
  try { return new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' }) } catch { return '' }
}

function RelatedCard({ post }: { post: BlogListItem }) {
  return (
    <Link to={`/blog/${post.slug}`} className="group flex flex-col bg-white rounded-xl border border-gray-100 shadow-sm hover:shadow-md hover:border-gray-200 transition-all overflow-hidden">
      <div className="aspect-[16/9] w-full" style={post.heroImageUrl ? undefined : { background: post.heroGradient }}>
        {post.heroImageUrl && <img src={post.heroThumbUrl || post.heroImageUrl} alt={post.title} width={640} height={360} loading="lazy" className="h-full w-full object-cover" />}
      </div>
      <div className="p-4">
        <span className="text-[11px] font-semibold uppercase tracking-wide text-gold-600">{post.category}</span>
        <p className="font-semibold text-gray-900 text-sm mt-1 leading-snug group-hover:text-gold-700 transition-colors">{post.title}</p>
      </div>
    </Link>
  )
}

export function BlogPost() {
  const { slug } = useParams<{ slug: string }>()
  const { paywallEnabled } = useAppConfig()
  const seed = typeof window !== 'undefined' ? window.__BLOG_POST__ : undefined
  const { data, isLoading, isError } = useQuery({
    queryKey: ['blog-post', slug],
    queryFn: () => fetchBlogPost(slug!),
    enabled: !!slug,
    retry: false,
    initialData: seed && seed.post?.slug === slug ? seed : undefined,
    // Keep the current article visible while the next one loads (no skeleton
    // flash on in-app navigation). Direct loads render instantly from SSR data.
    placeholderData: keepPreviousData,
  })

  // Give each <h2> an id and collect a table of contents (jump links).
  const { bodyWithIds, toc } = useMemo(() => {
    const html = data?.post?.bodyHtml ?? ''
    const items: { id: string; text: string }[] = []
    const bodyWithIds = html.replace(/<h2([^>]*)>([\s\S]*?)<\/h2>/g, (_m, attrs: string, inner: string) => {
      const text = inner.replace(/<[^>]+>/g, '').trim()
      if (!text) return `<h2${attrs}>${inner}</h2>`
      const id = text.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '').slice(0, 60)
      items.push({ id, text })
      return `<h2 id="${id}"${attrs}>${inner}</h2>`
    })
    return { bodyWithIds, toc: items }
  }, [data?.post?.bodyHtml])

  if (isLoading) {
    return (
      <PageWrapper>
        <div className="max-w-3xl mx-auto px-4 py-12">
          <div className="h-8 w-3/4 bg-gray-100 rounded animate-pulse mb-4" />
          <div className="aspect-[16/9] rounded-2xl bg-gray-100 animate-pulse mb-6" />
          <div className="space-y-3">{Array.from({ length: 6 }).map((_, i) => <div key={i} className="h-4 bg-gray-100 rounded animate-pulse" />)}</div>
        </div>
      </PageWrapper>
    )
  }

  if (isError || !data?.post) {
    return (
      <PageWrapper>
        <Seo title="Article not found" description="This article could not be found." noindex path={`/blog/${slug ?? ''}`} noBrandSuffix />
        <div className="max-w-3xl mx-auto px-4 py-20 text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-3">Article not found</h1>
          <Link to="/blog" className="text-gold-600 font-medium">← Back to all guides</Link>
        </div>
      </PageWrapper>
    )
  }

  const { post, related } = data
  const graph: object[] = [{
    '@type': 'BlogPosting',
    headline: post.title,
    description: post.metaDescription,
    author: { '@type': 'Organization', name: post.author },
    editor: { '@type': 'Person', name: 'Rupert Lowe', jobTitle: 'Editor' },
    publisher: { '@type': 'Organization', name: 'FixDelivered', logo: { '@type': 'ImageObject', url: `${SITE}/apple-touch-icon.png` } },
    ...(post.publishedAt ? { datePublished: post.publishedAt } : {}),
    dateModified: post.updatedAt,
    image: post.heroImageUrl || `${SITE}/apple-touch-icon.png`,
    mainEntityOfPage: `${SITE}/blog/${post.slug}`,
    ...(post.keywords?.length ? { keywords: post.keywords.join(', ') } : {}),
  }]
  if (post.faqs?.length) {
    graph.push({
      '@type': 'FAQPage',
      mainEntity: post.faqs.map(f => ({ '@type': 'Question', name: f.q, acceptedAnswer: { '@type': 'Answer', text: f.a } })),
    })
  }

  return (
    <PageWrapper>
      <Seo
        title={post.metaTitle || post.title}
        description={post.metaDescription}
        path={`/blog/${post.slug}`}
        image={post.heroImageUrl}
        noBrandSuffix
        structuredData={{ '@context': 'https://schema.org', '@graph': graph }}
      />

      <article className="max-w-3xl mx-auto px-4 py-10">
        <nav className="flex items-center gap-1 text-sm text-gray-400 mb-5">
          <Link to="/blog" className="hover:text-gray-700">Advice &amp; guides</Link>
          <ChevronRight className="h-3.5 w-3.5" />
          <Link to={`/blog/category/${categorySlug(post.category)}`} className="text-gray-500 hover:text-gray-700">{post.category}</Link>
        </nav>

        <h1 className="text-3xl sm:text-4xl font-extrabold text-gray-900 leading-tight">{post.title}</h1>
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-gray-500 mt-4">
          <span>{post.author}</span>
          <span aria-hidden>·</span>
          <span>Updated {fmtDate(post.updatedAt)}</span>
          <span aria-hidden>·</span>
          <span className="inline-flex items-center gap-1"><Clock className="h-3.5 w-3.5" /> {post.readingMinutes} min read</span>
        </div>

        <div className="aspect-[16/9] w-full rounded-2xl overflow-hidden mt-6 mb-8" style={post.heroImageUrl ? undefined : { background: post.heroGradient }}>
          {post.heroImageUrl && <img src={post.heroThumbUrl || post.heroImageUrl} alt={post.title} width={640} height={360} fetchPriority="high" className="h-full w-full object-cover" />}
        </div>

        {toc.length >= 3 && (
          <nav className="mb-8 rounded-2xl border border-gray-100 bg-gray-50 p-5" aria-label="Table of contents">
            <p className="text-xs font-semibold uppercase tracking-wide text-gray-400 mb-2">On this page</p>
            <ul className="space-y-1.5">
              {toc.map(t => <li key={t.id}><a href={`#${t.id}`} className="text-sm text-trust-600 hover:underline">{t.text}</a></li>)}
            </ul>
          </nav>
        )}

        {/* bodyHtml is sanitised server-side before storage (see blogGenerator.cleanBody). */}
        <div className="article-body" dangerouslySetInnerHTML={{ __html: bodyWithIds }} />

        {post.faqs?.length > 0 && (
          <section className="mt-12">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Frequently asked questions</h2>
            <div className="space-y-3">
              {post.faqs.map((f, i) => (
                <details key={i} className="group bg-white rounded-xl border border-gray-100 shadow-sm p-4">
                  <summary className="font-semibold text-gray-900 cursor-pointer list-none flex items-center justify-between">
                    {f.q}
                    <ChevronRight className="h-4 w-4 text-gray-400 group-open:rotate-90 transition-transform" />
                  </summary>
                  <p className="text-gray-600 mt-2 text-sm leading-relaxed">{f.a}</p>
                </details>
              ))}
            </div>
          </section>
        )}

        {/* Author / reviewer — E-E-A-T */}
        <div className="mt-12 rounded-2xl border border-gray-100 bg-gray-50 p-5 flex gap-4">
          <div className="w-11 h-11 rounded-full bg-gold-100 flex items-center justify-center flex-shrink-0 text-gold-700 font-bold text-sm">FD</div>
          <div className="text-sm">
            <p className="font-semibold text-gray-900">Written by the FixDelivered Team</p>
            <p className="text-gray-500 mt-1 leading-relaxed">
              Our editors research every guide against manufacturer instructions, UK trade standards (Gas Safe, WaterSafe)
              and the real-world experience of the reviewed plumbers on our platform.
            </p>
            <p className="text-gray-400 mt-2 text-xs">Reviewed by Rupert Lowe, Editor &middot; Last updated {fmtDate(post.updatedAt)}</p>
          </div>
        </div>

        {/* CTA */}
        <div className="mt-8 rounded-2xl bg-gray-900 text-white p-6 sm:p-8">
          <h2 className="text-xl font-bold">Need a plumber now?</h2>
          <p className="text-gray-300 mt-2 text-sm max-w-xl">
            {paywallEnabled
              ? "If it's beyond a quick fix, tell us your emergency and FixDelivered will find and confirm an available local plumber — usually within the hour. The booking fee is refundable if we can't find you someone."
              : "If it's beyond a quick fix, tell us your emergency and FixDelivered will find and confirm an available local plumber — usually within the hour. Completely free right now, no booking fee."}
          </p>
          <Link to="/" className="inline-flex items-center gap-2 mt-4 bg-emergency-600 hover:bg-emergency-700 text-white font-semibold px-5 py-3 rounded-xl transition-colors">
            Get emergency help <ArrowRight className="h-4 w-4" />
          </Link>
        </div>

        {related.length > 0 && (
          <section className="mt-12">
            <h2 className="text-xl font-bold text-gray-900 mb-4">Related guides</h2>
            <div className="grid gap-4 sm:grid-cols-3">
              {related.map(p => <RelatedCard key={p.slug} post={p} />)}
            </div>
          </section>
        )}
      </article>
    </PageWrapper>
  )
}
